import moment, { Moment } from "moment-timezone";
import { getKitchenTimezone } from "./timezone";

export type DishPromo = {
  days?: string[] | Record<string, boolean>;
  startTime?: string;
  endTime?: string;
  hideOutsideWindow?: boolean;
  featureDuringPromo?: boolean;
};

const TIME_FORMATS = [
  "hh:mmA",
  "h:mmA",
  "hh:mm A",
  "h:mm A",
  "HH:mm",
  "H:mm",
];

const parsePromoTime = (value?: string): Moment | null => {
  const raw = value?.trim();
  if (!raw) {
    return null;
  }

  for (const format of TIME_FORMATS) {
    const parsed = moment(raw, format, true);
    if (parsed.isValid()) {
      return parsed;
    }
  }

  const loose = moment(raw, TIME_FORMATS);
  return loose.isValid() ? loose : null;
};

const normalizePromoDays = (days?: DishPromo["days"]): string[] => {
  if (Array.isArray(days)) {
    return days.map((day) => String(day).trim()).filter(Boolean);
  }

  if (days && typeof days === "object") {
    return Object.entries(days)
      .filter(([, enabled]) => Boolean(enabled))
      .map(([day]) => String(day).trim())
      .filter(Boolean);
  }

  return [];
};

export const normalizeDishPromo = (
  promo?: DishPromo | null
): DishPromo | null => {
  if (!promo || typeof promo !== "object") {
    return null;
  }

  const days = normalizePromoDays(promo.days);
  const startTime = promo.startTime?.trim();
  const endTime = promo.endTime?.trim();

  if (!days.length || !startTime || !endTime) {
    return null;
  }

  return {
    days,
    startTime,
    endTime,
    hideOutsideWindow: Boolean(promo.hideOutsideWindow),
    featureDuringPromo: Boolean(promo.featureDuringPromo),
  };
};

const isDayInPromoDays = (days: string[], at: Moment): boolean => {
  const weekday = at.format("dddd").toLowerCase();
  return days.some((day) => day.toLowerCase() === weekday);
};

const toMomentAt = (
  at?: Moment | Date | string | null,
  timezone?: string
): Moment => {
  const tz = getKitchenTimezone(timezone);
  return at == null ? moment.tz(tz) : moment.tz(at, tz);
};

/** Same open-window style as kitchen hours in closedKitchenCheck. */
export const isPromoWindowActiveAt = (
  promo?: DishPromo | null,
  at?: Moment | Date | string | null,
  timezone?: string
): boolean => {
  const normalized = normalizeDishPromo(promo);
  if (!normalized) {
    return false;
  }

  const when = toMomentAt(at, timezone);
  if (!isDayInPromoDays(normalized.days as string[], when)) {
    return false;
  }

  const start = parsePromoTime(normalized.startTime);
  const end = parsePromoTime(normalized.endTime);
  if (!start || !end) {
    return false;
  }

  const timeOnly = moment(when.format("HH:mm"), "HH:mm");
  return (
    start.isSameOrBefore(timeOnly, "minute") && end.isAfter(timeOnly, "minute")
  );
};

/** Same open-window style as kitchen hours in closedKitchenCheck. */
export const isPromoWindowActive = (
  promo?: DishPromo | null,
  timezone?: string
): boolean => isPromoWindowActiveAt(promo, null, timezone);

const getDishPromo = (dish: { promo?: DishPromo | null }) =>
  normalizeDishPromo(dish?.promo);

/** Dish is eligible to appear anywhere on the online menu. */
export const isDishVisibleWithPromoAt = (
  dish: { promo?: DishPromo | null },
  at?: Moment | Date | string | null,
  timezone?: string
): boolean => {
  const promo = getDishPromo(dish);
  if (!promo) {
    return true;
  }
  if (!promo.hideOutsideWindow) {
    return true;
  }
  return isPromoWindowActiveAt(promo, at, timezone);
};

export const isDishVisibleWithPromo = (
  dish: { promo?: DishPromo | null },
  timezone?: string
): boolean => isDishVisibleWithPromoAt(dish, null, timezone);

/**
 * Promo item still listed on the menu but outside its window
 * (hideOutsideWindow is false).
 */
export const isDishPromoUnavailableAt = (
  dish: { promo?: DishPromo | null },
  at?: Moment | Date | string | null,
  timezone?: string
): boolean => {
  const promo = getDishPromo(dish);
  if (!promo || promo.hideOutsideWindow) {
    return false;
  }
  return !isPromoWindowActiveAt(promo, at, timezone);
};

export const isDishPromoUnavailable = (
  dish: { promo?: DishPromo | null },
  timezone?: string
): boolean => isDishPromoUnavailableAt(dish, null, timezone);

/** Dish should render in the top featured section (excluded from its category). */
export const isDishFeaturedPromo = (
  dish: { promo?: DishPromo | null },
  timezone?: string
): boolean => {
  const promo = getDishPromo(dish);
  return Boolean(
    promo &&
      promo.featureDuringPromo &&
      isPromoWindowActive(promo, timezone) &&
      isDishVisibleWithPromo(dish, timezone)
  );
};

const WEEKDAY_ORDER = [
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
  "Sunday",
];

const formatDisplayTime = (value: string): string => {
  const parsed = parsePromoTime(value);
  return parsed ? parsed.format("h:mmA") : value;
};

const formatPromoDaysLabel = (days: string[]): string => {
  const indices = days
    .map((day) =>
      WEEKDAY_ORDER.findIndex(
        (weekday) => weekday.toLowerCase() === day.toLowerCase()
      )
    )
    .filter((index) => index >= 0)
    .sort((a, b) => a - b);

  if (indices.length === 0) {
    return days.join(", ");
  }

  const ranges: Array<[number, number]> = [];
  let rangeStart = indices[0];
  let rangeEnd = indices[0];

  for (let i = 1; i < indices.length; i += 1) {
    if (indices[i] === rangeEnd + 1) {
      rangeEnd = indices[i];
    } else {
      ranges.push([rangeStart, rangeEnd]);
      rangeStart = indices[i];
      rangeEnd = indices[i];
    }
  }
  ranges.push([rangeStart, rangeEnd]);

  return ranges
    .map(([start, end]) =>
      start === end
        ? WEEKDAY_ORDER[start]
        : `${WEEKDAY_ORDER[start]} - ${WEEKDAY_ORDER[end]}`
    )
    .join(", ");
};

/** e.g. "Only available Tuesday - Friday 11:30AM–6:00PM." */
export const formatPromoAvailabilityText = (
  promo?: DishPromo | null
): string | null => {
  const normalized = normalizeDishPromo(promo);
  if (!normalized) {
    return null;
  }

  const startTime = formatDisplayTime(normalized.startTime!);
  const endTime = formatDisplayTime(normalized.endTime!);
  const daysLabel = formatPromoDaysLabel(normalized.days as string[]);

  return `Only available ${daysLabel} ${startTime}–${endTime}.`;
};
